import "./Navbar.css";

import { NavLink, useMatch, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLogout } from "../../hooks/useLogout";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCatalogCategoriesContext } from "../../hooks/useCatalogCategoriesContext";
import { useCartContext } from "../../hooks/useCartContext";

import Dropdown from "../Dropdown/Dropdown";
import Button from "../Button/Button";
import DropdownItem from "../DropdownItem/DropdownItem";
import { apiFetch } from "../../utils/api";
import { SUPPORTED_LANGUAGES } from "../../utils/translationUtils";


const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { logout } = useLogout();
  const { user } = useAuthContext();
  const { categories, locale: categoriesLocale, dispatch } = useCatalogCategoriesContext();
  const { items: cartItems } = useCartContext();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const catalogCategoryMatch = useMatch("/catalog/:id");
  const catalogItemMatch = useMatch("/catalog/item/:id");
  const catalogActive = catalogCategoryMatch || catalogItemMatch;

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (isHomePage) return; // Home fetches categories via /api/public/home
    if (categories && categories.length > 0 && categoriesLocale === i18n.language) return;
    const fetchNavigation = async () => {
      const response = await apiFetch("/api/navigation/", {
        headers: { Locale: i18n.language },
      });
      const json = await response.json();
      if (response.ok) {
        dispatch({ type: "SET_CATEGORIES", payload: json.response.categories, locale: i18n.language });
      }
    };
    fetchNavigation();
  }, [dispatch, i18n.language, isHomePage]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      setMenuOpen(false);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header className={scrolled ? "navbar-scrolled" : ""}>
      <div className="container">
        <NavLink to="/" className="nav-logo">
          Lutsero
        </NavLink>

        {/* Desktop nav */}
        <nav className="nav-desktop">
          <NavLink to="/" end>{t("nav.home")}</NavLink>

          <Dropdown
            buttonText={t("nav.catalog")}
            active={!!catalogActive}
            content={
              <>
                {categories && categories.map((category) => (
                  <DropdownItem key={category._id}>
                    <NavLink to={`/catalog/${category._id}`} className="dropdown-link">
                      {category.title}
                    </NavLink>
                  </DropdownItem>
                ))}
              </>
            }
          />

          <NavLink to="/blog">{t("nav.blog")}</NavLink>
          <NavLink to="/contact">{t("nav.contact")}</NavLink>

          <span className="nav-separator" />

          {SUPPORTED_LANGUAGES.map((lang) => (
            <Button
              key={lang}
              variant="icon"
              className={`nav-lang-btn${i18n.language === lang ? " nav-lang-active" : ""}`}
              onClick={() => i18n.changeLanguage(lang)}
            >
              {lang.toUpperCase()}
            </Button>
          ))}

          <NavLink to="/cart" className="nav-cart" aria-label="Cart">
            <span className="material-symbols-outlined">shopping_cart</span>
            {cartItems.length > 0 && (
              <span className="nav-cart-badge">{cartItems.length}</span>
            )}
          </NavLink>

          {user && (
            <>
              <span className="nav-separator" />
              <NavLink to="/categories">{t("nav.categories")}</NavLink>
              <NavLink to="/admin/blog">{t("nav.blogAdmin")}</NavLink>
              <span className="nav-logout" onClick={logout} role="button" tabIndex={0}>{t("nav.logout")}</span>
            </>
          )}
        </nav>

        {/* Mobile right side */}
        <div className="nav-mobile-actions">
          <NavLink to="/cart" className="nav-cart" aria-label="Cart">
            <span className="material-symbols-outlined">shopping_cart</span>
            {cartItems.length > 0 && (
              <span className="nav-cart-badge">{cartItems.length}</span>
            )}
          </NavLink>

          <Button
            variant="icon"
            className="nav-burger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(o => !o)}
          >
            <span key={menuOpen ? "close" : "menu"} className="material-symbols-outlined nav-burger-icon">
              {menuOpen ? "close" : "menu"}
            </span>
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <>
          <div className="nav-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="nav-mobile-menu">
            <div className="nav-mobile-section">
              <NavLink to="/" end>{t("nav.home")}</NavLink>
              <NavLink to="/blog">{t("nav.blog")}</NavLink>
              <NavLink to="/contact">{t("nav.contact")}</NavLink>
            </div>

            {categories && categories.length > 0 && (
              <div className="nav-mobile-section">
                <span className="nav-mobile-section-label">{t("nav.catalog")}</span>
                {categories.map((category) => (
                  <NavLink key={category._id} to={`/catalog/${category._id}`} className="nav-mobile-category">
                    {category.title}
                  </NavLink>
                ))}
              </div>
            )}

            <div className="nav-mobile-section">
              <span className="nav-mobile-section-label">{t("nav.language")}</span>
              <div className="nav-mobile-langs">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Button
                    key={lang}
                    variant="icon"
                    className={`nav-lang-btn${i18n.language === lang ? " nav-lang-active" : ""}`}
                    onClick={() => i18n.changeLanguage(lang)}
                  >
                    {lang.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>

            {user && (
              <div className="nav-mobile-section">
                <NavLink to="/categories">{t("nav.categories")}</NavLink>
                <NavLink to="/admin/blog">{t("nav.blogAdmin")}</NavLink>
                <span className="nav-logout" onClick={logout} role="button" tabIndex={0}>{t("nav.logout")}</span>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;
